<template>
  <!--
    Standardized multi-category picker. v-model is a string[] of category slugs;
    the first slug is treated as primary by callers. `options` is the DB-backed,
    parent-then-child-ordered category list (see PortfolioView categoryOptions).
  -->
  <div class="cat-multi">
    <div class="cat-multi-chips">
      <label
        v-for="c in options"
        :key="c.slug"
        class="cat-pill"
        :class="{ on: modelValue.includes(c.slug) }"
        :style="c.parent_slug ? 'margin-left:0.75rem' : ''"
      >
        <input type="checkbox" :checked="modelValue.includes(c.slug)" @change="toggle(c.slug)" />
        <span v-if="c.parent_label" class="pv-muted">{{ c.parent_label }} › </span>{{ c.label }}
      </label>
      <span v-if="!options.length" class="pv-muted">No categories defined yet.</span>
    </div>
  </div>
</template>

<script setup lang="ts">
interface CategoryOption { slug: string; label: string; parent_slug?: string | null; parent_label?: string | null }

const props = defineProps<{
  modelValue: string[]
  options: CategoryOption[]
}>()
const emit = defineEmits<{ (e: 'update:modelValue', v: string[]): void }>()

function toggle(slug: string) {
  const next = [...props.modelValue]
  const i = next.indexOf(slug)
  if (i >= 0) next.splice(i, 1)
  else next.push(slug)
  emit('update:modelValue', next)
}
</script>

<style scoped>
.cat-multi { display: flex; flex-direction: column; gap: 0.4rem; }
.cat-multi-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--border-default);
  border-radius: 999px;
  background: var(--bg-input);
  color: var(--text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;
}
.cat-pill input { margin: 0; }
.cat-pill.on {
  background: var(--accent, var(--status-converted-text));
  border-color: var(--accent, var(--status-converted-text));
  color: var(--text-primary);
}
.pv-muted { color: var(--text-muted); }
</style>
